import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    Typography,
    Button,
    TextField,
    Divider,
    Checkbox,
    FormControlLabel,
    Snackbar,
    Autocomplete
} from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { reindexDocument } from "../../../store/actions/documentAction";
import { searchOrdersElastic } from "../../../store/actions/orderActions";
import AppSnackbar from "../../../components/AppSnackbar";
import DraggablePaper from "../../../components/PaperComponent"

export default function ReindexModal({ open, sourceDoc, onClose, onSave }) {
    const dispatch = useDispatch();

    const notifySave = async (success) => {
        if (typeof onSave === "function") {
            await onSave(success);
        }
    };

    const closeModal = () => {
        if (typeof onClose === "function") {
            onClose();
        }
    };

    const [selectedType, setSelectedType] = useState(null);
    const [rows, setRows] = useState([]);
    const loggedInUser = useSelector((state) => state?.authReducer?.user);
    const [moveOrder, setMoveOrder] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [loadingOrders, setLoadingOrders] = useState(false);

    const [searchFields, setSearchFields] = useState({
        controlNo: "",
        reference: "",
        unit: "",
        chassis: ""
    });

    const [snack, setSnack] = useState({
        open: false,
        message: "",
        severity: "info"
    });

    const handleOrderSearch = () => {
        setLoadingOrders(true);
        const hasValue = Object.values(searchFields).some(Boolean);
        if (!hasValue) return;

        const payload = [
            {
                "clause": "AND",
                "field": "locationNumber",
                "operand": "IS",
                "value": sourceDoc.locationNumber,
                "format": "Number"
            },
            {
                "clause": "OR",
                "field": "unitNumber",
                "operand": "IS",
                "value": searchFields.unit,
                "format": "Alphanumeric"
            },
            {
                "clause": "OR",
                "field": "billingReference",
                "operand": "IS",
                "value": searchFields.reference,
                "format": "Alphanumeric"
            },
            {
                "clause": "OR",
                "field": "chassisNumber",
                "operand": "IS",
                "value": searchFields.chassis,
                "format": "Alphanumeric"
            },
            {
                "clause": "OR",
                "field": "controlNumber",
                "operand": "IS",
                "value": searchFields.controlNo,
                "format": "Number"
            }
        ].filter(
            (item) =>
                item.value !== undefined &&
                item.value !== null &&
                String(item.value).trim() !== ""
        );
        dispatch(searchOrdersElastic(payload)).then((res) => {
            // Results are in Redux
            if (res.status === 'success') {
                const hits = res.data.hits || [];
                const uniqueRows = Array.from(
                    new Map(
                        hits.map(item => [
                            item._id, // key = unique _id
                            {
                                id: item._id,              // 👈 REQUIRED for MUI DataGrid
                                ...item._source
                            }
                        ])
                    ).values()
                );
                setLoadingOrders(false);
                setRows(uniqueRows);
            }
        });
    };

    const handleSave = async () => {
        try {
            setSnack({
                open: true,
                message: "Reindexing document...",
                severity: "info"
            });

            const payload = buildReindexPayload({
                // locationNumber: sourceDoc.locationNumber,
                originalControlNo: sourceDoc.controlNumber,
                originalDocTypeId: sourceDoc.docTypeId,
                originalDocTypeCode: sourceDoc.docTypeCode,
                originalFilename: sourceDoc.originalFilename,
                newControlNo: selectedOrder?.controlNumber || 0,
                newDocTypeId: selectedType?.id || 0,
                newDocTypeCode: selectedType?.document_type_code || 0,
                userId: loggedInUser?.userId || 0 // TEMP fallback
            });
            if (!payload) return;
            
            const apiRes = await reindexDocument(payload, sourceDoc.locationNumber);
            if (apiRes.status === 'success') {
                setSnack({
                    open: true,
                    message: "Document reindexed successfully",
                    severity: "success"
                });
                closeModal();
                void notifySave(true);
            } else {
                await notifySave(false);
                throw new Error(apiRes?.message || "Reindex API failed");
            }
        } catch (err) {
            await notifySave(false);
            setSnack({
                open: true,
                message: "Failed to reindex document",
                severity: "error"
            });
        }
    };

    function buildReindexPayload(input) {
        const {
            locationNumber,
            originalControlNo,
            originalDocTypeId,
            originalDocTypeCode,
            originalFilename,
            newControlNo,
            newDocTypeId,
            newDocTypeCode,
            userId
        } = input;

        const isControlChanged =
            newControlNo > 0 &&
            newControlNo !== originalControlNo;

        const isDocTypeChanged =
            newDocTypeId > 0 &&
            newDocTypeId !== originalDocTypeId;



        let updateType;

        if (isControlChanged && isDocTypeChanged) {
            updateType = 3; // change both
        } else if (isControlChanged) {
            updateType = 1; // move order only
        } else if (isDocTypeChanged) {
            updateType = 2; // change doc type only
        } else {
            throw new Error("No changes detected for re-index");
        }

        const payload = {
            updateType,
            locationNumber,
            source: {
                controlNo: originalControlNo,
                docTypeId: originalDocTypeId,
                docTypeCode: originalDocTypeCode,
                originalFilename
            },
            target: {},
            requestedBy: {
                userId
            }
        };

        // Target control #
        if (isControlChanged) {
            payload.target.controlNo = newControlNo;
        }

        // Target doc type
        if (isDocTypeChanged) {
            payload.target.docTypeId = newDocTypeId;
            payload.target.docTypeCode = newDocTypeCode;
        }

        return payload;
    }

    const docTypeChanged =
        selectedType &&
        selectedType.id !== sourceDoc?.docTypeId;

    const orderChanged =
        moveOrder &&
        selectedOrder?.controlNumber &&
        selectedOrder.controlNumber !== sourceDoc?.controlNumber;

    const canSave = Boolean(docTypeChanged || orderChanged);

    const columns = [
        { field: "controlNumber", headerName: "Control #", width: 120 },
        { field: "shipperAgent", headerName: "Order by Name", width: 130 },
        { field: "billingReference", headerName: "Reference", width: 130 },
        { field: "unit", headerName: "Unit", width: 110 },
        { field: "shipper", headerName: "Shipper Name", width: 130 },
        { field: "consignee", headerName: "Consignee Name", flex: 1 }
    ];

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="xl"
                fullWidth
                PaperProps={{
                    sx: {
                        height: "75vh",
                        maxWidth: "1150px"
                    }
                }}
                PaperComponent={DraggablePaper}
                aria-labelledby="draggable-dialog-title"
            >
                <DialogTitle
                    style={{ cursor: 'move' }}
                    id="draggable-dialog-title"
                    borderBottom={1}
                    borderColor={`#ddd`}
                >
                    <Box>
                        <Typography variant="h6" fontWeight={600}>
                            Reindex Document
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Control #: <b>{sourceDoc?.controlNumber}</b> &nbsp;|&nbsp;
                            Doc Type: <b>{sourceDoc?.docAbbrevation}</b> &nbsp;|&nbsp;
                            Doc Code: <b>{sourceDoc?.docTypeCode || "-"}</b>
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ p: 0, height: "100%" }}>
                    <Box display="flex" flexDirection="column" height="100%">

                        {/* DOCUMENT TYPE */}
                        <Box p={2}>
                            <Typography fontSize={13} mb={0.5}>
                                Document Type
                            </Typography>

                            {/* Replace with your real dropdown */}
                            <Autocomplete
                                options={sourceDoc?.docTypeData || []}
                                value={selectedType}
                                onChange={(e, v) => setSelectedType(v)}
                                getOptionLabel={(o) => o?.abbrevation || ''}
                                isOptionEqualToValue={(option, value) => option.id == value.id}
                                renderInput={(params) => (
                                    <TextField {...params} label="Document Type" />
                                )}
                            />
                            <Typography fontSize={11} color="text.secondary" mt={0.5}>
                                You may change the document type and move the document to another order in a single action.
                            </Typography>
                        </Box>

                        {/* MOVE TO ORDER TOGGLE */}
                        <Box px={2}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={moveOrder}
                                        onChange={(e) => setMoveOrder(e.target.checked)}
                                    />
                                }
                                label="Move document to another order"
                            />
                        </Box>

                        {/* MOVE TO ORDER SECTION */}
                        {moveOrder && (
                            <>
                                <Divider />

                                <Box display="flex" flex={1} minHeight={0}>

                                    {/* LEFT — SEARCH */}
                                    <Box
                                        width="320px"
                                        p={2}
                                        borderRight="1px solid #ddd"
                                        bgcolor="#fafafa"
                                    >
                                        <Typography fontSize={13} fontWeight={600} mb={1}>
                                            Search Order
                                        </Typography>

                                        <TextField
                                            label="Control #"
                                            size="small"
                                            fullWidth
                                            value={searchFields.controlNo}
                                            onChange={(e) =>
                                                setSearchFields({
                                                    ...searchFields,
                                                    controlNo: e.target.value
                                                })
                                            }
                                            sx={{ mb: 1 }}
                                        />

                                        <TextField
                                            label="Reference #"
                                            size="small"
                                            fullWidth
                                            value={searchFields.reference}
                                            onChange={(e) =>
                                                setSearchFields({
                                                    ...searchFields,
                                                    reference: e.target.value
                                                })
                                            }
                                            sx={{ mb: 1 }}
                                        />

                                        <TextField
                                            label="Unit #"
                                            size="small"
                                            fullWidth
                                            value={searchFields.unit}
                                            onChange={(e) =>
                                                setSearchFields({
                                                    ...searchFields,
                                                    unit: e.target.value
                                                })
                                            }
                                            sx={{ mb: 1 }}
                                        />

                                        <TextField
                                            label="Chassis #"
                                            size="small"
                                            fullWidth
                                            value={searchFields.chassis}
                                            onChange={(e) =>
                                                setSearchFields({
                                                    ...searchFields,
                                                    chassis: e.target.value
                                                })
                                            }
                                            sx={{ mb: 2 }}
                                        />

                                        <Button
                                            variant="contained"
                                            fullWidth
                                            onClick={handleOrderSearch}
                                        >
                                            Search
                                        </Button>
                                    </Box>

                                    {/* RIGHT — RESULTS */}
                                    <Box
                                        flex={1}
                                        p={2}
                                        display="flex"
                                        flexDirection="column"
                                        minHeight={0}
                                    >
                                        <Typography fontSize={13} mb={1}>
                                            Select an order
                                        </Typography>

                                        <DataGridPro
                                            rows={rows}
                                            columns={columns}
                                            getRowId={(row) => row.controlNumber}
                                            hideFooter
                                            density="compact"
                                            sx={{ flex: 1 }}
                                            rowSelectionModel={
                                                selectedOrder
                                                    ? [selectedOrder.controlNumber]
                                                    : []
                                            }
                                            onRowClick={(params) =>
                                                setSelectedOrder(params.row)
                                            }
                                            loading={loadingOrders}
                                        />

                                        {/* ORDER SUMMARY */}

                                    </Box>
                                </Box>
                            </>
                        )}

                        {/* FOOTER */}
                        <Box
                            display="flex"
                            justifyContent="flex-end"
                            p={2}
                            borderTop="1px solid #ddd"
                        >
                            <Button onClick={onClose} sx={{ mr: 1 }}>
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                disabled={!canSave}
                                onClick={handleSave}
                            >
                                Save
                            </Button>
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>

            <AppSnackbar
                open={snack.open}
                message={snack.message}
                severity={snack.severity}
                onClose={() =>
                    setSnack({ open: false, message: "", severity: "info" })
                }
            />
        </>
    );
}
